import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { damDeckProfile } from '../../../site/damDeck';
import { boxSolid, pyramidOverRing } from '../solids';
import { DAM_BUILDINGS } from './damBuildings';
import { toLayout, type PlanFrame } from './planFrame';

/**
 * The stone railing along the Dam's two water faces, the Damrak's and the Rokin's: a parapet under
 * a coping, between piers that each carry a pointed finial. The user asked for it on 6 October
 * 2026, "a stone railing with ornaments, on both sides", after the 1538 painting, which draws a
 * dark low structure with light points on it on the crest beside the sluice. That it ran each
 * face from bank to bank, its section, the piers' spacing and the finials' form are estimates.
 */
export const DAM_RAILING = {
  /** Its outer side stands this far inside the dam's face, so the two are not one plane. */
  setbackM: 0.1,
  heightM: 1,
  thickM: 0.4,
  coping: { overM: 0.06, heightM: 0.12 },
  pier: { everyM: 3.6, sideM: 0.56, heightM: 1.3 },
  finial: { riseM: 0.42, insetM: 0.06 },
  /**
   * The bank at each end of a face, where no railing stands: the Rokin is narrower than the dam is
   * long, and the banks beside it are the way on and off the crest. Read from the layout's water;
   * damRailing.test.ts holds each end to the water's edge.
   */
  bankM: { rokin: { west: 15.6, east: 7.9 }, damrak: { west: 3.05, east: 2.75 } },
  /** A house whose wall comes this near a face stands on it: the railing stops at the house. */
  houseReachM: 1.2,
  /** No railing is built for a shorter stretch between two houses. */
  leastRunM: 1.5,
  /** How far each part runs into the one it stands against, so no two share a face. */
  sinkM: 0.05,
} as const;

/** One straight railing in the dam's plan: along `v`, from `from` to `to`, with its piers. */
export interface RailingRun {
  readonly v: number;
  readonly from: number;
  readonly to: number;
  /** The middle of each pier along the run: one at either end, the rest evenly between. */
  readonly piers: readonly number[];
}

interface Deck {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
}

function piersOf(from: number, to: number): number[] {
  const { everyM, sideM } = DAM_RAILING.pier;
  const first = from + sideM / 2;
  const span = to - from - sideM;
  const bays = Math.max(1, Math.round(span / everyM));
  return Array.from({ length: bays + 1 }, (_, i) => first + (span * i) / bays);
}

/** The stretches of one face that no house stands on. `side` is +1 for the face at `v1`. */
function openStretches(deck: Deck, side: 1 | -1): [number, number][] {
  const face = side > 0 ? deck.v1 : deck.v0;
  const taken = DAM_BUILDINGS.flatMap((b): [number, number][] => {
    const [alongU, alongV] = b.faces === 'damrak' ? [b.front, b.depth] : [b.depth, b.front];
    const gap = side > 0 ? face - (b.v + alongV / 2) : b.v - alongV / 2 - face;
    return gap < DAM_RAILING.houseReachM ? [[b.u - alongU / 2, b.u + alongU / 2]] : [];
  }).sort((a, b) => a[0] - b[0]);
  const bank = side > 0 ? DAM_RAILING.bankM.damrak : DAM_RAILING.bankM.rokin;
  const end = deck.u1 - bank.east;
  const open: [number, number][] = [];
  let from = deck.u0 + bank.west;
  for (const [a, b] of taken) {
    if (a - from >= DAM_RAILING.leastRunM) open.push([from, a]);
    from = Math.max(from, b);
  }
  if (end - from >= DAM_RAILING.leastRunM) open.push([from, end]);
  return open;
}

/** The railings of a dam whose crest lies between these bounds of its plan. */
export function damRailingRuns(deck: Deck): RailingRun[] {
  const inside = DAM_RAILING.setbackM + DAM_RAILING.pier.sideM / 2;
  return ([-1, 1] as const).flatMap((side) =>
    openStretches(deck, side).map(([from, to]) => ({
      v: (side > 0 ? deck.v1 : deck.v0) - side * inside,
      from,
      to,
      piers: piersOf(from, to),
    }))
  );
}

/** The railings standing on the crest at `crestY`, in the dam's own frame. */
export function buildDamRailing(
  builder: MeshBuilder,
  styles: { readonly stone: VertexStyle; readonly trim: VertexStyle },
  frame: Frame,
  deck: Deck,
  crestY: number
): void {
  const { heightM, thickM, coping, pier, finial, sinkM } = DAM_RAILING;
  const foot = crestY - sinkM;
  for (const run of damRailingRuns(deck)) {
    const length = run.to - run.from;
    const stone: VertexStyle = { ...styles.stone, extent: [length, heightM] };
    const trim: VertexStyle = { ...styles.trim, extent: [length, coping.heightM] };
    // The parapet and its coping end inside the two end piers.
    boxSolid(
      builder,
      stone,
      frame,
      [run.from + sinkM, foot, -(run.v + thickM / 2)],
      [run.to - sinkM, crestY + heightM, -(run.v - thickM / 2)]
    );
    boxSolid(
      builder,
      trim,
      frame,
      [run.from + 2 * sinkM, crestY + heightM - sinkM, -(run.v + thickM / 2 + coping.overM)],
      [run.to - 2 * sinkM, crestY + heightM + coping.heightM, -(run.v - thickM / 2 - coping.overM)],
      { top: true, bottom: true }
    );
    const half = pier.sideM / 2;
    const cap = half - finial.insetM;
    for (const u of run.piers) {
      boxSolid(
        builder,
        { ...stone, extent: [pier.sideM, pier.heightM] },
        frame,
        [u - half, foot, -(run.v + half)],
        [u + half, crestY + pier.heightM, -(run.v - half)]
      );
      pyramidOverRing(
        builder,
        { roof: trim, wall: trim },
        frame,
        [
          [u - cap, run.v - cap],
          [u + cap, run.v - cap],
          [u + cap, run.v + cap],
          [u - cap, run.v + cap],
        ],
        crestY + pier.heightM - sinkM,
        { plan: [u, run.v], y: crestY + pier.heightM + finial.riseM }
      );
    }
  }
}

function ringOf(plan: PlanFrame, run: RailingRun): Ring {
  const half = DAM_RAILING.pier.sideM / 2;
  return [
    toLayout(plan, [run.from, run.v - half]),
    toLayout(plan, [run.to, run.v - half]),
    toLayout(plan, [run.to, run.v + half]),
    toLayout(plan, [run.from, run.v + half]),
  ];
}

/** The railings in plan: they stop a walker at the crest's edge, as they stopped a cart. */
export function damRailingRings(landmark: Landmark): readonly Ring[] {
  const deck = damDeckProfile(landmark);
  return damRailingRuns(deck).map((run) => ringOf(deck.plan, run));
}
